/**
 * The MOB (man overboard) button, living in the right-hand control stack where
 * the mic used to sit.
 *
 * At rest it's a normal frosted-white HUD button with a ring-lifebuoy icon, so it
 * reads as "the life-ring" and sits quietly with the other controls rather than
 * shouting a red block the whole time. MOB is still safety-critical, so we keep
 * the conventional chartplotter behaviour: a single press drops the mark at the
 * vessel immediately (no arm-to-activate step to slow a real emergency), and the
 * separate Cancel clears it behind a quick 'are you sure?' confirm so a live MOB
 * can't be lost to one stray press. A firm triple
 * buzz confirms activation through a glove or a wet hand. Once a MOB is running
 * the button flips to a red pulsing state so there's no missing that it's live.
 *
 * It reuses the shared drop/clear path (mobActions), the same one the Safety menu
 * uses, so the mark lands in the safety store and rides through the boat-icon
 * self-heal and a context loss without being lost. Being a plain child of the
 * control stack, it also inherits that stack's stacking order, so an open menu or
 * sheet covers it rather than the button floating on top.
 */
import { useSafetyStore } from '@/store/safety';
import { useShellStore } from '@/store/shell';
import { haptic } from '@/ui/haptics';
import { playMobConfirmation } from '@/ui/alarmSound';
import { LifebuoyIcon } from './icons';
import { dropMobAtVessel, clearMob } from './mobActions';

export function MobButton() {
  const mob = useSafetyStore((s) => s.mob);
  const showToast = useShellStore((s) => s.showToast);
  const showConfirm = useShellStore((s) => s.showConfirm);
  const openSheet = useShellStore((s) => s.openSheet);
  const active = mob != null;

  const onDrop = () => {
    if (dropMobAtVessel()) {
      haptic('mob');
      // A distinct rising "got it" tone alongside the buzz, so the crew know the
      // mark registered even in the chaos of a real MOB.
      playMobConfirmation();
      showToast('MOB dropped at the vessel — steering to the casualty');
    } else {
      showToast('MOB — no vessel position yet');
    }
  };

  const onCancel = () => {
    // Cancelling loses the MOB position and stops the return, so make it a
    // deliberate two-tap action rather than one stray press. The confirm is a
    // single tap to go through (this is time-sensitive), and 'Not now' keeps the
    // MOB running. Opening the prompt is a normal tap (select); the firmer commit
    // buzz lands on the prompt's Cancel-MOB button itself.
    haptic('select');
    showConfirm({
      message: 'Cancel the man overboard? This clears the mark and ends the return.',
      confirmLabel: 'Cancel MOB',
      onConfirm: () => {
        clearMob();
        showToast('MOB cleared');
      },
    });
  };

  // A second press of a live MOB opens the drift & search menu (drift the
  // casualty on the tide and wind, then work a search pattern). The mark itself
  // is only ever cleared through the separate Cancel, so this can't lose it.
  const onOpenMenu = () => {
    haptic('select');
    openSheet('mobsearch');
  };

  if (active) {
    return (
      <>
        <button
          type="button"
          className="acp-ctrl acp-mob-ctrl active"
          aria-live="assertive"
          aria-label="Man overboard active — open drift and search"
          title="Man overboard active — tap for drift estimate and search patterns"
          onClick={onOpenMenu}
        >
          <LifebuoyIcon />
        </button>
        <button
          type="button"
          className="acp-ctrl acp-mob-cancel"
          onClick={onCancel}
          aria-label="Cancel man overboard and clear the mark"
        >
          Cancel
        </button>
      </>
    );
  }

  return (
    <button
      type="button"
      className="acp-ctrl acp-mob-ctrl"
      aria-label="Man overboard — drop a mark at the vessel and start the return"
      title="Man overboard — drops a mark at the vessel and starts the return"
      onClick={onDrop}
    >
      <LifebuoyIcon />
    </button>
  );
}
